import React, { useState, useEffect } from "react";
import {
  X,
  Info,
  Crown,
  Eye,
  Save,
  Pencil,
  Plus,
  KeyRound,
} from "lucide-react";
import {
  Usuario,
  CategoriaProfissional,
  CATEGORIA_LABELS,
  CATEGORIA_REGISTRO_MAP,
  UFS_BRASIL,
  categoriaRequerRegistro,
  getTipoRegistro,
} from "../../../types";
import { theme } from "../../../shared/styles/theme";

interface UsuarioFormProps {
  onSubmit: (usuario: Omit<Usuario, "id" | "criadoEm">) => void;
  onCancel: () => void;
  initialData?: Usuario;
  usuarioLogadoId: string;
}

interface UsuarioFormData {
  nome: string;
  email: string;
  tipo: "administrador" | "usuario" | "convidado";

  // ⭐ NOVOS CAMPOS
  categoria: CategoriaProfissional;
  registroNumero: string;
  registroUF: string;

  // CAMPOS LEGADOS
  especialidade: string;

  // 🏥 DADOS DO SOLICITANTE (B211 — formulários auxiliares SUS)
  solCnsProfissional: string;
  solCpfProfissional: string;
  solUnidadeSaude: string;
  solCnes: string;
  solMunicipioAtendimento: string;
  solCodigoIBGE: string;
  solUfAtendimento: string;

  ativo: boolean;
}

export default function UsuarioForm({
  onSubmit,
  onCancel,
  initialData,
  usuarioLogadoId,
}: UsuarioFormProps) {
  const [formData, setFormData] = useState<UsuarioFormData>({
    nome: "",
    email: "",
    tipo: "usuario",
    categoria: "medico", // Default
    registroNumero: "",
    registroUF: "",
    especialidade: "",
    solCnsProfissional: "",
    solCpfProfissional: "",
    solUnidadeSaude: "",
    solCnes: "",
    solMunicipioAtendimento: "",
    solCodigoIBGE: "",
    solUfAtendimento: "",
    ativo: true,
  });

  const [erros, setErros] = useState<
    Partial<Record<keyof UsuarioFormData, string>>
  >({});
  const modoEdicao = !!initialData;

  // ✏️ PREENCHER DADOS SE ESTIVER EDITANDO
  useEffect(() => {
    if (initialData) {
      setFormData({
        nome: initialData.nome,
        email: initialData.email,
        tipo: initialData.tipo,
        categoria: initialData.categoria || "medico",
        registroNumero: initialData.registroProfissional?.numero || "",
        registroUF: initialData.registroProfissional?.uf || "",
        especialidade: initialData.especialidade || "",
        solCnsProfissional: initialData.dadosSolicitante?.cnsProfissional || "",
        solCpfProfissional: initialData.dadosSolicitante?.cpfProfissional || "",
        solUnidadeSaude: initialData.dadosSolicitante?.unidadeSaude || "",
        solCnes: initialData.dadosSolicitante?.cnes || "",
        solMunicipioAtendimento:
          initialData.dadosSolicitante?.municipioAtendimento || "",
        solCodigoIBGE: initialData.dadosSolicitante?.codigoIBGE || "",
        solUfAtendimento: initialData.dadosSolicitante?.ufAtendimento || "",
        ativo: initialData.ativo,
      });
    }
  }, [initialData]);

  // 📝 ATUALIZAR CAMPO
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Limpar erro do campo
    setErros((prev) => ({ ...prev, [name]: undefined }));
  };

  // 🔄 TRATAMENTO ESPECIAL PARA MUDANÇA DE CATEGORIA
  const handleCategoriaChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const novaCategoria = e.target.value as CategoriaProfissional;

    setFormData((prev) => ({
      ...prev,
      categoria: novaCategoria,
      // Limpar campos de registro se não for necessário
      registroNumero: categoriaRequerRegistro(novaCategoria)
        ? prev.registroNumero
        : "",
      registroUF: categoriaRequerRegistro(novaCategoria) ? prev.registroUF : "",
    }));

    // Limpar erros relacionados
    setErros((prev) => ({
      ...prev,
      categoria: undefined,
      registroNumero: undefined,
      registroUF: undefined,
    }));
  };

  // ✅ VALIDAR FORMULÁRIO
  const validarFormulario = (): boolean => {
    const novosErros: Partial<Record<keyof UsuarioFormData, string>> = {};

    // Validações básicas
    if (!formData.nome.trim()) {
      novosErros.nome = "Nome é obrigatório";
    }

    if (!formData.email.trim()) {
      novosErros.email = "Email é obrigatório";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      novosErros.email = "Email inválido";
    }

    if (!formData.categoria) {
      novosErros.categoria = "Categoria profissional é obrigatória";
    }

    // ⭐ VALIDAR REGISTRO PROFISSIONAL (se necessário)
    if (categoriaRequerRegistro(formData.categoria)) {
      if (!formData.registroNumero.trim()) {
        const tipoRegistro = getTipoRegistro(formData.categoria);
        novosErros.registroNumero = `Número do ${tipoRegistro} é obrigatório`;
      }

      if (!formData.registroUF) {
        novosErros.registroUF = "UF é obrigatória";
      }
    }

    setErros(novosErros);
    return Object.keys(novosErros).length === 0;
  };

  // 💾 SALVAR
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validarFormulario()) {
      return;
    }

    const usuario: any = {
      nome: formData.nome.trim(),
      email: formData.email.trim().toLowerCase(),
      tipo: formData.tipo,
      categoria: formData.categoria,
      ativo: formData.ativo,
    };

    // ⭐ ADICIONAR REGISTRO PROFISSIONAL (se aplicável)
    if (
      categoriaRequerRegistro(formData.categoria) &&
      formData.registroNumero.trim()
    ) {
      const tipoRegistro = getTipoRegistro(formData.categoria);
      usuario.registroProfissional = {
        numero: formData.registroNumero.trim(),
        uf: formData.registroUF,
        tipo: tipoRegistro!,
      };
    }

    // Adicionar especialidade (para médicos principalmente)
    if (formData.especialidade?.trim()) {
      usuario.especialidade = formData.especialidade.trim();
    }

    // 🏥 DADOS DO SOLICITANTE (B211) — só grava o que foi preenchido
    const dadosSolicitante: Record<string, string> = {};
    if (formData.solCnsProfissional.trim())
      dadosSolicitante.cnsProfissional = formData.solCnsProfissional.trim();
    if (formData.solCpfProfissional.trim())
      dadosSolicitante.cpfProfissional = formData.solCpfProfissional.trim();
    if (formData.solUnidadeSaude.trim())
      dadosSolicitante.unidadeSaude = formData.solUnidadeSaude.trim();
    if (formData.solCnes.trim()) dadosSolicitante.cnes = formData.solCnes.trim();
    if (formData.solMunicipioAtendimento.trim())
      dadosSolicitante.municipioAtendimento =
        formData.solMunicipioAtendimento.trim();
    if (formData.solCodigoIBGE.trim())
      dadosSolicitante.codigoIBGE = formData.solCodigoIBGE.trim();
    if (formData.solUfAtendimento)
      dadosSolicitante.ufAtendimento = formData.solUfAtendimento;
    if (Object.keys(dadosSolicitante).length > 0) {
      usuario.dadosSolicitante = dadosSolicitante;
    }

    onSubmit(usuario);
  };

  // ========================================
  // ESTILOS
  // ========================================
  const inputStyle = {
    width: "100%",
    padding: "12px",
    border: `1px solid ${theme.gray[300]}`,
    borderRadius: theme.borderRadius.md,
    fontSize: "14px",
    fontFamily: theme.fonts.sans,
    boxSizing: "border-box" as const,
  };

  const inputErrorStyle = {
    ...inputStyle,
    border: `2px solid #dc2626`,
  };

  const labelStyle = {
    display: "block",
    fontSize: "14px",
    fontWeight: "600",
    color: theme.gray[700],
    marginBottom: "6px",
    fontFamily: theme.fonts.sans,
  };

  // ========================================
  // VERIFICAR SE PRECISA MOSTRAR CAMPOS
  // ========================================
  const mostrarRegistro = categoriaRequerRegistro(formData.categoria);
  const tipoRegistro = getTipoRegistro(formData.categoria);

  return (
    <div
      style={{
        background: "white",
        borderRadius: theme.borderRadius.xl,
        boxShadow: theme.shadows.md,
        padding: "24px",
      }}
    >
      <h3
        style={{
          fontSize: "22px",
          fontWeight: "bold",
          color: theme.gray[800],
          marginBottom: "8px",
          fontFamily: theme.fonts.sans,
        }}
      >
        {modoEdicao ? (
          <>
            <Pencil size={14} style={{ marginRight: "6px" }} /> Editar Usuário
          </>
        ) : (
          <>
            <Plus size={14} style={{ marginRight: "6px" }} /> Novo Usuário
          </>
        )}
      </h3>

      {/* ⭐ INFORMAÇÃO SOBRE SENHA AUTOMÁTICA */}
      {!modoEdicao && (
        <div
          style={{
            background: "#fef3c7",
            border: `2px solid #f59e0b`,
            borderRadius: theme.borderRadius.md,
            padding: "12px 16px",
            marginBottom: "24px",
          }}
        >
          <p
            style={{
              fontSize: "13px",
              color: "#92400e",
              margin: 0,
              fontFamily: theme.fonts.sans,
            }}
          >
            <KeyRound size={14} style={{ marginRight: "6px" }} />{" "}
            <strong>Senha Temporária:</strong> Uma senha será gerada
            automaticamente e exibida após o cadastro. Copie e entregue ao
            usuário.
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "20px",
            marginBottom: "20px",
          }}
        >
          {/* ========================================
              DADOS BÁSICOS
          ======================================== */}

          {/* NOME */}
          <div>
            <label style={labelStyle}>Nome Completo *</label>
            <input
              type="text"
              name="nome"
              value={formData.nome}
              onChange={handleChange}
              style={erros.nome ? inputErrorStyle : inputStyle}
            />
            {erros.nome && (
              <p
                style={{
                  color: "#dc2626",
                  fontSize: "12px",
                  marginTop: "4px",
                  fontFamily: theme.fonts.sans,
                }}
              >
                {erros.nome}
              </p>
            )}
          </div>

          {/* EMAIL */}
          <div>
            <label style={labelStyle}>Email *</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              disabled={modoEdicao} // ⭐ Não permite editar email
              style={
                modoEdicao
                  ? {
                      ...inputStyle,
                      background: theme.gray[100],
                      cursor: "not-allowed",
                    }
                  : erros.email
                  ? inputErrorStyle
                  : inputStyle
              }
            />
            {erros.email && (
              <p
                style={{
                  color: "#dc2626",
                  fontSize: "12px",
                  marginTop: "4px",
                  fontFamily: theme.fonts.sans,
                }}
              >
                {erros.email}
              </p>
            )}
            {modoEdicao && (
              <p
                style={{
                  color: theme.gray[500],
                  fontSize: "11px",
                  marginTop: "4px",
                  fontFamily: theme.fonts.sans,
                }}
              >
                <Info size={12} style={{ marginRight: "4px" }} /> Email não pode
                ser alterado
              </p>
            )}
          </div>

          {/* TIPO DE USUÁRIO */}
          <div>
            <label style={labelStyle}>Tipo de Usuário *</label>
            <select
              name="tipo"
              value={formData.tipo}
              onChange={handleChange}
              style={inputStyle}
            >
              <option value="usuario">Usuário</option>
              <option value="administrador">Administrador</option>
              <option value="convidado">Convidado</option>
            </select>
          </div>

          {/* ⭐ CATEGORIA PROFISSIONAL */}
          <div>
            <label style={labelStyle}>Categoria Profissional *</label>
            <select
              name="categoria"
              value={formData.categoria}
              onChange={handleCategoriaChange}
              style={erros.categoria ? inputErrorStyle : inputStyle}
            >
              {(Object.keys(CATEGORIA_LABELS) as CategoriaProfissional[]).map(
                (cat) => (
                  <option key={cat} value={cat}>
                    {CATEGORIA_LABELS[cat]}
                  </option>
                )
              )}
            </select>
            {erros.categoria && (
              <p
                style={{
                  color: "#dc2626",
                  fontSize: "12px",
                  marginTop: "4px",
                  fontFamily: theme.fonts.sans,
                }}
              >
                {erros.categoria}
              </p>
            )}
          </div>

          {/* ========================================
              CAMPOS DINÂMICOS - REGISTRO PROFISSIONAL
          ======================================== */}

          {mostrarRegistro && (
            <>
              {/* NÚMERO DO REGISTRO */}
              <div>
                <label style={labelStyle}>Número do {tipoRegistro} *</label>
                <input
                  type="text"
                  name="registroNumero"
                  value={formData.registroNumero}
                  onChange={handleChange}
                  style={erros.registroNumero ? inputErrorStyle : inputStyle}
                  placeholder={`Ex: 123456`}
                />
                {erros.registroNumero && (
                  <p
                    style={{
                      color: "#dc2626",
                      fontSize: "12px",
                      marginTop: "4px",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    {erros.registroNumero}
                  </p>
                )}
              </div>

              {/* UF DO REGISTRO */}
              <div>
                <label style={labelStyle}>UF do {tipoRegistro} *</label>
                <select
                  name="registroUF"
                  value={formData.registroUF}
                  onChange={handleChange}
                  style={erros.registroUF ? inputErrorStyle : inputStyle}
                >
                  <option value="">Selecione...</option>
                  {UFS_BRASIL.map((uf) => (
                    <option key={uf} value={uf}>
                      {uf}
                    </option>
                  ))}
                </select>
                {erros.registroUF && (
                  <p
                    style={{
                      color: "#dc2626",
                      fontSize: "12px",
                      marginTop: "4px",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    {erros.registroUF}
                  </p>
                )}
              </div>
            </>
          )}

          {/* ========================================
              CAMPO DINÂMICO - ESPECIFICAÇÃO "OUTROS"
          ======================================== */}

          {/* ========================================
              ESPECIALIDADE (OPCIONAL)
          ======================================== */}

          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelStyle}>
              Especialidade / Área de Atuação (opcional)
            </label>
            <input
              type="text"
              name="especialidade"
              value={formData.especialidade}
              onChange={handleChange}
              style={inputStyle}
              placeholder={
                formData.categoria === "medico"
                  ? "Ex: Cirurgião Geral, Hepatologia, etc"
                  : formData.categoria === "enfermeiro"
                  ? "Ex: Enfermagem em Transplantes, UTI, etc"
                  : "Ex: Área de especialização"
              }
            />
          </div>

          {/* ========================================
              🏥 DADOS DO SOLICITANTE (B211 — formulários SUS)
          ======================================== */}

          <div
            style={{
              gridColumn: "1 / -1",
              borderTop: `1px solid ${theme.gray[200]}`,
              paddingTop: "16px",
              marginTop: "4px",
            }}
          >
            <p
              style={{
                fontSize: "15px",
                fontWeight: "700",
                color: theme.gray[800],
                margin: "0 0 4px 0",
                fontFamily: theme.fonts.sans,
              }}
            >
              🏥 Dados para formulários do SUS (opcional)
            </p>
            <p
              style={{
                fontSize: "12px",
                color: theme.gray[500],
                margin: "0 0 4px 0",
                fontFamily: theme.fonts.sans,
              }}
            >
              Usados para preencher automaticamente a ficha GAL e outros
              formulários auxiliares. Preencha uma vez e o sistema reaproveita.
            </p>
          </div>

          {/* CNS DO PROFISSIONAL */}
          <div>
            <label style={labelStyle}>CNS do Profissional</label>
            <input
              type="text"
              name="solCnsProfissional"
              value={formData.solCnsProfissional}
              onChange={handleChange}
              style={inputStyle}
              placeholder="Cartão Nacional de Saúde (15 dígitos)"
            />
          </div>

          {/* CPF DO PROFISSIONAL */}
          <div>
            <label style={labelStyle}>CPF do Profissional</label>
            <input
              type="text"
              name="solCpfProfissional"
              value={formData.solCpfProfissional}
              onChange={handleChange}
              style={inputStyle}
              placeholder="Exigido nos formulários de carga viral (HBV/HCV/HIV)"
            />
          </div>

          {/* UNIDADE DE SAÚDE */}
          <div>
            <label style={labelStyle}>Unidade de Saúde (solicitante)</label>
            <input
              type="text"
              name="solUnidadeSaude"
              value={formData.solUnidadeSaude}
              onChange={handleChange}
              style={inputStyle}
              placeholder="Nome completo, sem abreviaturas"
            />
          </div>

          {/* CNES */}
          <div>
            <label style={labelStyle}>CNES da Unidade</label>
            <input
              type="text"
              name="solCnes"
              value={formData.solCnes}
              onChange={handleChange}
              style={inputStyle}
              placeholder="Cód. Nacional de Estab. de Saúde"
            />
          </div>

          {/* MUNICÍPIO DE ATENDIMENTO */}
          <div>
            <label style={labelStyle}>Município de Atendimento</label>
            <input
              type="text"
              name="solMunicipioAtendimento"
              value={formData.solMunicipioAtendimento}
              onChange={handleChange}
              style={inputStyle}
              placeholder="Ex: Porto Velho"
            />
          </div>

          {/* CÓDIGO IBGE */}
          <div>
            <label style={labelStyle}>Código IBGE do Município</label>
            <input
              type="text"
              name="solCodigoIBGE"
              value={formData.solCodigoIBGE}
              onChange={handleChange}
              style={inputStyle}
              placeholder="Ex: 1100205"
            />
          </div>

          {/* UF DO ATENDIMENTO */}
          <div>
            <label style={labelStyle}>UF da Unidade</label>
            <select
              name="solUfAtendimento"
              value={formData.solUfAtendimento}
              onChange={handleChange}
              style={inputStyle}
            >
              <option value="">Selecione...</option>
              {UFS_BRASIL.map((uf) => (
                <option key={uf} value={uf}>
                  {uf}
                </option>
              ))}
            </select>
          </div>

          {/* ========================================
              ATIVO/BLOQUEADO (APENAS EDIÇÃO)
          ======================================== */}

          {modoEdicao && initialData?.id !== usuarioLogadoId && (
            <div style={{ gridColumn: "1 / -1" }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                  fontFamily: theme.fonts.sans,
                }}
              >
                <input
                  type="checkbox"
                  name="ativo"
                  checked={formData.ativo}
                  onChange={handleChange}
                  style={{ width: "18px", height: "18px", cursor: "pointer" }}
                />
                <span style={{ fontSize: "14px", color: theme.gray[700] }}>
                  Usuário ativo (desmarque para bloquear)
                </span>
              </label>
            </div>
          )}
        </div>

        {/* ========================================
            INFORMAÇÃO DINÂMICA
        ======================================== */}

        {mostrarRegistro && (
          <div
            style={{
              background: theme.primary[50],
              border: `1px solid ${theme.primary[200]}`,
              borderRadius: theme.borderRadius.md,
              padding: "12px 16px",
              marginBottom: "20px",
            }}
          >
            <p
              style={{
                fontSize: "13px",
                color: theme.primary[800],
                margin: 0,
                fontFamily: theme.fonts.sans,
              }}
            >
              <Info size={12} style={{ marginRight: "4px" }} />{" "}
              <strong>{CATEGORIA_LABELS[formData.categoria]}</strong> requer
              registro no <strong>{tipoRegistro}</strong>
            </p>
          </div>
        )}

        {/* ========================================
            BOTÕES
        ======================================== */}

        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: "flex-end",
            marginTop: "24px",
          }}
        >
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: "12px 24px",
              background: theme.gray[200],
              color: theme.gray[700],
              border: "none",
              borderRadius: theme.borderRadius.md,
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              fontFamily: theme.fonts.sans,
            }}
          >
            <X size={14} style={{ marginRight: "6px" }} /> Cancelar
          </button>

          <button
            type="submit"
            style={{
              padding: "12px 24px",
              background: theme.primary[600],
              color: "white",
              border: "none",
              borderRadius: theme.borderRadius.md,
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              fontFamily: theme.fonts.sans,
            }}
          >
            <Save size={14} style={{ marginRight: "6px" }} />{" "}
            {modoEdicao ? "Salvar Alterações" : "Cadastrar Usuário"}
          </button>
        </div>
      </form>
    </div>
  );
}
